import {Injectable, ModuleWithProviders, NgModule} from '@angular/core';
import {Observable} from 'rxjs';
import {LoggedInUserModel} from '../../../shared/models/user.model';
import {UnitModel} from '../../../shared/models/unit.model';
import {GroupModel} from '../../../shared/models/group.model';
import {UserRoleModel} from '../../../shared/models/user-role.model';
import {UnitService} from '../../api/unit/unit.service';
import {GroupService} from '../../api/group/group.service';
import {UserService} from '../../api/users/users.service';
import {SearchFilterService} from '../search-filter/search-filter.service';
import {LocalStorageService} from 'ngx-webstorage';
import { DeviceProductService } from '../../api/device-product/device-produc.service';

@Injectable()
export class SessionService {
  user: LoggedInUserModel = null;
  units: UnitModel[];
  groups: GroupModel[];
  linRoles: UserRoleModel[];
  groupRoles: UserRoleModel[];
  unitRoles: UserRoleModel[];
  targetState: string = null;
  appSettings;
  pageHistory;

  constructor(private unitService: UnitService,
              private groupService: GroupService,
              private userService: UserService,
              private searchFilterService: SearchFilterService,
              private storage: LocalStorageService) {
    this.appSettings = this.getAppSettings();
    this.pageHistory = this.getPageHistory();
  }

  setUser(user) {
    return new Observable((observer) => {
      user.customer = {};
      user.lin = {};

      user.Roles.filter(function (role) {

        if (role.RoleType.indexOf('Unit') > -1 || role.RoleType.indexOf('Group') > -1) {
          user.customer.user = true;
        } else if (role.RoleType.indexOf('Lin') > -1) {
          user.lin.user = true;
        }

        switch (role.RoleType) {
          case 'GroupAdmin':
            user.customer.admin = true;
            user.customer.groupAdmin = true;
            break;
          case 'UnitAdmin':
            user.customer.admin = true;
            user.customer.unitAdmin = true;
            break;
          case 'LinAdmin':
            user.lin.admin = true;
            break;
          case 'LinTechnician':
            user.lin.technician = true;
            break;
          case 'LinDriver':
            user.lin.driver = true;
            break;
          case 'LinService':
            user.lin.service = true;
            break;
          case 'LinSale':
            user.lin.sale = true;
            break;
          case 'LinRecycle':
            user.lin.recycle = true;
            break;
          case 'LinDelivery':
            user.lin.delivery = true;
            break;
          case 'LinDEPSolutions':
            user.lin.dep = true;
            break;
          case 'LinRecycleExtraPersonnel':
            user.lin.recycleExtra = true;
            break;
          case 'LinDeliveryExtraPersonnel':
            user.lin.deliveryExtra = true;
            break;
          case 'LinOrderManager':
            user.lin.orderManager = true;
            break;
          default:
            break;
        }
      });
      this.user = user;
      observer.next(this.user);
      observer.complete();
    });
  }

  getUser() {
    return this.user;
  }

  isLinUser(): boolean {
    return (this.user && this.user.lin && this.user.lin.user);
  }

  isLinAdmin(): boolean {
    return (this.user && this.user.lin && this.user.lin.admin);
  }

  getUnits() {
    if (this.units) {
      return this.units;
    } else {
      this.unitService.get().subscribe(result => {
        return this.units = result.Units;
      });
    }
  }

  getGroups(noGroup?) {
    return new Observable(observer => {
      if (this.groups) {
        observer.next(this.groups);
        observer.complete();
      } else {
        return this.groupService.get().subscribe(result => {
          this.groups = result.Groups;
          if (noGroup) {
            return this.unitService.get({filter: this.searchFilterService.is('Group.GroupId', 'Null')}).subscribe(unitResult => {
              if (unitResult.Units.length > 0) {
                const singleUnits = {
                  GroupName: 'NO_GROUP',
                  GroupId: 'NO_GROUP',
                  singleUnits: true,
                  Units: unitResult.Units
                };
                this.groups.push(singleUnits);
                observer.next(this.groups);
                observer.complete();
              } else {
                observer.next(this.groups);
                observer.complete();
              }
            });
          } else {
            this.groups = result.Groups;
            observer.next(this.groups);
            observer.complete();
          }
        });
      }
    });
  }

  getLinRoles() {
    return new Observable(observer => {
      if (this.linRoles) {
        observer.next(this.linRoles);
        observer.complete();
      } else {
        this.userService.getLinRoles().subscribe(result => {
          this.linRoles = result;
          observer.next(this.linRoles);
          observer.complete();
        });
      }
    });
  }

  getGroupRoles() {
    return new Observable(observer => {
      if (this.groupRoles) {
        observer.next(this.groupRoles);
        observer.complete();
      } else {
        this.userService.getGroupRoles().subscribe(result => {
          this.groupRoles = result;
          observer.next(this.groupRoles);
          observer.complete();
        });
      }
    });
  }

  getUnitRoles() {
    return new Observable(observer => {
      if (this.unitRoles) {
        observer.next(this.unitRoles);
        observer.complete();
      } else {
        this.userService.getUnitRoles().subscribe(result => {
          this.unitRoles = result;
          observer.next(this.unitRoles);
          observer.complete();
        });
      }
    });
  }

  getAppSettings() {
    return this.storage.retrieve('appsettings') || {};
  }

  setAppSetting(setting, data) {
    this.appSettings[setting] = data;
    this.storage.store('appsettings', this.appSettings);
  }

  getPageHistory() {
    return this.storage.retrieve('pagehistory') || [];
  }

  setPageHistory(data) {
    const duplicate = this.pageHistory.findIndex(page => (data.data.caseNumber === page.data.caseNumber && data.data.section === page.data.section && data.data.page === page.data.page));
    if (duplicate > -1) {
      this.pageHistory.splice(duplicate, 1);
    }
    this.pageHistory.unshift(data);
    if (this.pageHistory.length > 8) {
      this.pageHistory.length = 8;
    }
    this.storage.store('pagehistory', this.pageHistory);
  }
}

@NgModule({
  imports: [],
  providers: [
    UnitService,
    GroupService,
    UserService,
    SearchFilterService,
    DeviceProductService
  ],
  declarations: [],
  exports: []
})
export class SessionModule {
  static forRoot(): ModuleWithProviders<SessionModule> {
    return {
      ngModule: SessionModule,
      providers: [SessionService]
    };
  }
}
